import React, { useEffect, useMemo, useState } from "react";
import {
  PiCheckCircleLight,
  PiFileTextLight,
  PiDatabaseLight,
  PiUploadSimpleLight,
  PiMagnifyingGlassLight,
  PiRocketLaunchLight,
  PiClockLight,
  PiStackLight,
  PiWarningCircleLight,
} from "react-icons/pi";
import MiniLoadingToCheckmark from "./animations/MiniLoadingToCheckmark";
import LogoSpinner from "./LogoSpinner";
import Button from "./TailwindComponents/Button";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../store";
import { showModal, hideModal } from "../../store/actions/modalActions";
export type UiStepStatus = "Pending" | "In Progress" | "Completed" | "Failed" | "Skipped";

export interface IndexStep {
  id: string;            
  label: string;       
  status: UiStepStatus;
  messages: string[];    
}

interface Props {
  title?: string;
  steps: IndexStep[];
  connected: boolean;            
  complete: boolean;             
  isConnecting: boolean;      
  ok: boolean | null;            
  summary?: string;              
  startedAt?: string | number;   
  onCancel?: () => void;  
  type?: string
}

const prettyTime = (secs: number) =>
  secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;

const iconFor = (stepId: string, status: UiStepStatus): React.ReactNode => {
  if (status === "Completed") return <PiCheckCircleLight className="h-5 w-5 text-green-500" />;
  if (status === "Failed") return <PiWarningCircleLight className="h-5 w-5 text-red-500" />;
  if (status === "In Progress") return <MiniLoadingToCheckmark isLoading={true} />;

  switch (stepId) {
    case "quota":
      return <PiStackLight className="h-5 w-5 text-gray-500" />;
    case "create":
      return <PiFileTextLight className="h-5 w-5 text-gray-500" />;
    case "upload":
      return <PiUploadSimpleLight className="h-5 w-5 text-gray-500" />;
    case "index":
    case "setup_index":
      return <PiDatabaseLight className="h-5 w-5 text-gray-500" />;
    case "datasource":
    case "setup_datasource":
      return <PiDatabaseLight className="h-5 w-5 text-gray-500" />;
    case "indexer":
    case "setup_indexer":
      return <PiMagnifyingGlassLight className="h-5 w-5 text-gray-500" />;
    case "run":
      return <PiRocketLaunchLight className="h-5 w-5 text-gray-500" />;
    case "wait":
      return <PiClockLight className="h-5 w-5 text-gray-500" />;
    case "commit":
      return <PiFileTextLight className="h-5 w-5 text-gray-500" />;
    default:
      return <PiFileTextLight className="h-5 w-5 text-gray-500" />;
  }
};

const SSECreationProgress: React.FC<Props> = ({
  title = "Index creation in progress",
  steps,
  connected,
  complete,
  ok,
  summary,
  startedAt,
  onCancel,
  type= "index"
}) => {
  // elapsed timer (controlled by startedAt prop)
  const [elapsed, setElapsed] = useState<number>(0);
  const {t} = useTranslation()
  const dispatch = useAppDispatch();

  const handleCancelWithConfirmation = () => {
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      message: t('Cancel Process'),
      subMsg: t('This will stop the current process and any data created during this process may be deleted.'),
      icon: <PiWarningCircleLight className="w-6 h-6 text-orange-600" />,
      isDest: true,
      onConfirm: () => {
        dispatch(hideModal());
        if (onCancel) {
          onCancel();
        }
      },
      onCancel: () => dispatch(hideModal())
    }));
  };

  useEffect(() => {
    if (!startedAt) return;
    const startMs = typeof startedAt === "string" ? new Date(startedAt).getTime() : Number(startedAt);
    const tick = setInterval(() => setElapsed(Math.max(0, Math.floor((Date.now() - startMs) / 1000))), 1000);
    return () => clearInterval(tick);
  }, [startedAt]);

  // percentage based on completed + in-progress steps
  // Uses (total + 1) as denominator so first-step completion = 25% with 3 steps
  const percent = useMemo(() => {
    const total = steps.length || 1;
    const done = steps.filter((s) => s.status === "Completed").length;
    const inProgress = steps.filter((s) => s.status === "In Progress").length;
    const failed = steps.some((s) => s.status === "Failed");
    if (complete || failed) return 100;
    const progressValue = done + (inProgress * 0.15);
    return Math.round((progressValue / (total + 1)) * 100);
  }, [steps, complete]);

  // “connecting” placeholder
  if (!connected && !complete) {
    return (
      <div className="flex items-center justify-center h-64">
        <LogoSpinner size="medium" />
      </div>
    );
  }

  return (
    <div className="w-[50vw] max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
          {complete && ok ? (
            <PiCheckCircleLight className="h-6 w-6 text-green-500" />
          ) : (
            <MiniLoadingToCheckmark isLoading={!complete} />
          )}
          {complete ? (ok ? t("Index creation complete") : t("Index creation failed")) : title}
        </h3>
        <div className="flex items-center gap-3">
          {startedAt && (
            <span className="text-sm text-gray-600 dark:text-gray-400">{prettyTime(elapsed)}</span>
          )}
          {onCancel && !complete && (
            <Button variant="neutral" size="small" onClick={handleCancelWithConfirmation}>
              {t("Cancel")}
            </Button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-6">
        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
          <div
            style={{ width: `${percent}%` }}
            className="h-full bg-gradient-to-r from-blue-500 to-blue-900 transition-all duration-700 ease-in-out"
          />
        </div>
        <span className="text-xs text-gray-600 dark:text-gray-400 mt-1 block">
          {percent}% {t("Complete")} • {connected ? t("Connected") : t("Disconnected")}
        </span>
      </div>

      {/* Steps with vertical line */}
      <div className="relative">
        {/* Subtle vertical connector line behind icons */}
        <div className="absolute left-[9px] top-3 bottom-3 w-px bg-gray-200 dark:bg-gray-700 opacity-50" />
        
        <div className="space-y-4 pl-8">
          {steps.map((s, i) => (
            <div key={s.id} className="animate-fade-in-up relative" style={{ animationDelay: `${i * 0.06}s` }}>
              {/* Step indicator dot with white background to cover line */}
              <div className="absolute -left-8 top-0.5 flex items-center justify-center bg-gray-50 dark:bg-gray-900 rounded-full">
                {iconFor(s.id, s.status)}
              </div>
              
              <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <p className="font-medium">{s.label}</p>
                <span
                  className={
                    "ml-auto text-[10px] px-2 py-0.5 rounded-full " +
                    (s.status === "Completed"
                      ? "bg-green-100 text-green-700"
                      : s.status === "Failed"
                      ? "bg-red-100 text-red-700"
                      : s.status === "In Progress"
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-600")
                  }
                >
                  {s.status}
                </span>
              </div>

              {!!s.messages.length && (
                <div className="mt-3 space-y-1">
                  {s.messages.map((m, j) => (
                    <div
                      key={`${s.id}-m-${j}`}
                      className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 animate-fade-in-up"
                      style={{ animationDelay: `${(i + j) * 0.06}s` }}
                    >
                      <MiniLoadingToCheckmark isLoading={s.status === "In Progress" && j === s.messages.length - 1} />
                      <p className="overflow-hidden">{m}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      {!complete ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
         {t("This may take a few minutes. You can close this window.")}
        </p>
      ) : (
        <p
          className={
            "text-xs mt-4 " + (ok ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400")
          }
        >
          {summary || (ok ? t("Completed") : t("Failed"))}
        </p>
      )}
    </div>
  );
};

export default SSECreationProgress;
